/**
 * The admin section's pages. Adding an admin page is one entry here: it gets
 * its own URL (a route under the app shell, admins only), a link in the admin
 * sub-nav and in the phone's page picker. The page component is loaded lazily.
 * See docs/web.md, "Admin console".
 */
import type { LucideIcon } from "lucide-react";
import { Activity, Archive, BadgeCheck, Bell, Bot, BrainCircuit, Building2, CalendarClock, CreditCard, DatabaseZap, Eye, HeartPulse, Image as ImageIcon, Landmark, LayoutDashboard, Lightbulb, Mail, MailPlus, MailSearch, Megaphone, MessageCircle, Newspaper, Radar, ReceiptIndianRupee, RefreshCw, Rss, ScrollText, Send, Target, UserCheck, Users, UsersRound } from "lucide-react";
import { lazy, type ComponentType, type LazyExoticComponent } from "react";

import { parseAiSearch } from "../ai/search";
import { parseActivityLogSearch } from "./activityLogSearch";
import { parseAiPaperSearch } from "./aiPaperSearch";
import { parseEmailLogSearch } from "./emailLogSearch";
import { parseRegistersSearch } from "./registersSearch";

export const ADMIN_GROUPS = ["Overview", "People", "Money", "Content", "Operations", "Logs", "Settings"] as const;
export type AdminGroup = (typeof ADMIN_GROUPS)[number];

export interface AdminSection {
  /** Stable id (tests, the phone picker). */
  id: string;
  group: AdminGroup;
  /** In the sub-nav and the page heading. */
  label: string;
  icon: LucideIcon;
  /** Full app path, under /app/admin. */
  path: string;
  /** The line under the page heading; omit when the page has its own. */
  subtitle?: string;
  /** The page draws its own heading (with actions beside it). */
  ownHeader?: boolean;
  component: LazyExoticComponent<ComponentType>;
  /**
   * Platform roles that see the page (default: admins only). Every admin
   * route is admin-only on the server regardless.
   */
  roles?: string[];
  /** Parse and default the URL search params (as PageDef.validateSearch). */
  validateSearch?: (search: Record<string, unknown>) => object;
}

/** React.lazy over a named export. */
function page<M extends Record<string, unknown>>(load: () => Promise<M>, name: keyof M & string) {
  return lazy(async () => ({ default: (await load())[name] as ComponentType }));
}

export const ADMIN_ROOT = "/app/admin";

/** Sections switched off for now (none since the RA platform reopened, V063). */
export const DORMANT_SECTIONS: AdminSection[] = [];

export const ADMIN_SECTIONS: AdminSection[] = [
  {
    id: "overview",
    group: "Overview",
    label: "Overview",
    icon: LayoutDashboard,
    path: ADMIN_ROOT,
    subtitle: "Users, RAs to verify, payments, jobs and live subscriptions at a glance.",
    component: page(() => import("./OverviewPage"), "OverviewPage"),
    ownHeader: true,
    // `tab` only for the old ?tab= links, which redirect (AdminLayout)
    validateSearch: (s) => (typeof s.tab === "string" ? { tab: s.tab, q: typeof s.q === "string" ? s.q : "" } : {}),
  },
  // People
  {
    id: "users",
    group: "People",
    label: "Users",
    icon: Users,
    path: "/app/admin/users",
    subtitle: "Every account: create, edit, plans, AI-call access and sign-in state.",
    component: page(() => import("./UsersPage"), "UsersPage"),
    ownHeader: true,
    // q: the search box; f: the filter chip (investor, ra, trial, ...)
    validateSearch: (s) => ({
      ...(typeof s.q === "string" && s.q ? { q: s.q } : {}),
      ...(typeof s.f === "string" && s.f ? { f: s.f } : {}),
    }),
  },
  {
    // back with RA sign-up (V063): registrations that didn't match automatically,
    // and members asking to become RAs whose code couldn't be sent
    id: "verification",
    group: "People",
    label: "RA verification",
    icon: BadgeCheck,
    path: "/app/admin/verification",
    subtitle: "RAs whose registration didn't match SEBI's register automatically.",
    component: page(() => import("./VerificationPage"), "VerificationPage"),
    ownHeader: true,
  },
  {
    id: "sebi-registers",
    group: "People",
    label: "SEBI registers",
    icon: Building2,
    path: "/app/admin/sebi-registers",
    subtitle: "SEBI's Research Analyst and Investment Adviser registers, and who is on both.",
    component: page(() => import("./SebiRegistersPage"), "SebiRegistersPage"),
    // tab: ra (default) / ia / both; q: the search box; state; page
    validateSearch: parseRegistersSearch,
  },
  {
    // V066: every RA photo is approved here before anyone else sees it
    id: "photos",
    group: "People",
    label: "RA photos",
    icon: ImageIcon,
    path: "/app/admin/photos",
    subtitle: "Profile photos RAs uploaded: approve them before investors see them, or reject with a reason.",
    component: page(() => import("./PhotosPage"), "PhotosPage"),
    ownHeader: true,
  },
  {
    id: "team",
    group: "People",
    label: "Team activity",
    icon: UsersRound,
    path: "/app/admin/team",
    subtitle: "What the sales team did today: calls, status changes and follow-ups.",
    component: page(() => import("./TeamActivityTab"), "TeamActivityTab"),
  },
  // Money
  {
    id: "payments",
    group: "Money",
    label: "Payments",
    icon: ReceiptIndianRupee,
    path: "/app/admin/payments",
    subtitle: "Payments users submitted: approve or reject, with receipts.",
    component: page(() => import("./PaymentsTab"), "PaymentsTab"),
  },
  {
    // one page for the prices, launch offers and free trials (owner, 29 Oct 2026)
    id: "plans",
    group: "Money",
    label: "Plans & trials",
    icon: CreditCard,
    path: "/app/admin/plans",
    subtitle: "Who can sign up, the free trials, and each plan's price and launch offer.",
    component: page(() => import("./PlansPage"), "PlansAndTrialsPage"),
  },
  // Content
  {
    id: "broadcast",
    group: "Content",
    label: "Broadcast",
    icon: Megaphone,
    path: "/app/admin/broadcast",
    subtitle: "A message by app, email, WhatsApp and push, or a trade call to friends, and the history of both.",
    // tab: message (default) / paper (Trade call) / history; old "sent" links open the history
    validateSearch: (s) =>
      s.tab === "paper" || s.tab === "history" ? { tab: s.tab } : s.tab === "sent" ? { tab: "history" } : {},
    component: page(() => import("./AnnouncementsTab"), "BroadcastPage"),
  },
  {
    // V071: the invitation email to SEBI-registered Research Analysts (owner, 29 Oct 2026)
    id: "ra-emailer",
    group: "Content",
    label: "RA emailer",
    icon: MailPlus,
    path: "/app/admin/ra-emailer",
    subtitle:
      "The invitation email to SEBI-registered Research Analysts: the text, a test send, who gets it, and new RAs each day.",
    component: page(() => import("./RaEmailerPage"), "RaEmailerPage"),
  },
  {
    // V074: WhatsApp Business broadcasts and templates (owner, 31 Oct 2026)
    id: "whatsapp",
    group: "Content",
    label: "WhatsApp",
    icon: MessageCircle,
    path: "/app/admin/whatsapp",
    subtitle:
      "The sales chatbot's chats, templates for Meta to approve, every message sent, and the charges per message. Broadcasts are on Admin → Broadcast.",
    // tab: broadcast (default) / chats / chatbot / messages / templates / settings
    validateSearch: (s) =>
      ["chats", "chatbot", "messages", "templates", "settings"].includes(s.tab as string) ? { tab: s.tab as string } : {},
    component: page(() => import("./WhatsAppPage"), "WhatsAppPage"),
  },
  {
    // Market Radar's settings, delivery (app, email, push, WhatsApp) and activity (V089)
    id: "market-radar",
    group: "Content",
    label: "Market Radar",
    icon: Radar,
    path: "/app/admin/market-radar",
    subtitle:
      "Live alerts on unusual moves: which alerts go to whom, how they're sent (app, email, phone push, WhatsApp) and how many each person gets.",
    component: page(() => import("./MarketRadarPage"), "MarketRadarPage"),
  },
  {
    // the 08:45 and 16:00 Market Desk briefs: on/off, push and WhatsApp (V089)
    id: "daily-brief",
    group: "Content",
    label: "Daily brief",
    icon: Newspaper,
    path: "/app/admin/daily-brief",
    subtitle:
      "The morning and evening market brief: when it goes, what it includes, and who gets it on which channel.",
    component: page(() => import("./DailyBriefPage"), "DailyBriefPage"),
  },
  {
    // V112: the live match between the two paper traders: who sees it, tabs, text
    id: "ai-match",
    group: "Content",
    label: "AI Match",
    icon: Bot,
    path: "/app/admin/ai-match",
    subtitle:
      "The mascot and the live match between ALGO AI and AI Trader: who sees it, which tabs show, its text and name.",
    component: page(() => import("./AiMatchPage"), "AiMatchPage"),
  },
  {
    // V099: live news from the public feeds, judged (AI or rules), sent as alerts
    id: "market-news",
    group: "Content",
    label: "Market news",
    icon: Rss,
    path: "/app/admin/market-news",
    subtitle:
      "Live market-moving news from the public feeds: the sources, what counts as market-moving, which news goes to whom and how it's sent.",
    component: page(() => import("./MarketNewsAdminPage"), "MarketNewsAdminPage"),
  },
  {
    // V094: how RAs' calls reach investors: events and RA plans x channels, and the
    // admins' own WhatsApp copy (moved from WhatsApp -> Settings)
    id: "ra-calls",
    group: "Content",
    label: "RA calls",
    icon: Send,
    path: "/app/admin/ra-calls",
    subtitle:
      "How Research Analysts' calls and their updates reach investors: which events and RA plans use the app, email, phone push and WhatsApp, and your own copy.",
    component: page(() => import("./RaCallsPage"), "RaCallsPage"),
  },
  {
    // V093: what a Research Analyst hears about followers, clients and profile reviews
    id: "ra-notifications",
    group: "Content",
    label: "RA notifications",
    icon: UserCheck,
    path: "/app/admin/ra-notifications",
    subtitle:
      "What Research Analysts hear about their followers, clients and profile reviews, and on which channels (app, email, phone push, WhatsApp).",
    component: page(() => import("./RaNotificationsPage"), "RaNotificationsPage"),
  },
  {
    id: "ai-paper",
    group: "Content",
    label: "AI paper trades",
    icon: Bot,
    path: "/app/admin/ai-paper",
    subtitle:
      "Which instruments are paper traded, which setups have proven themselves, who sees them, and the notice shown with them.",
    component: page(() => import("./AiPaperPage"), "AiPaperPage"),
    validateSearch: parseAiPaperSearch,
  },
  {
    // V105 (owner, 2 Nov 2026): positional Gold / Silver option ideas, watched every
    // minute of the MCX session; alerts and paper positions only (admins for now)
    id: "watch-alert",
    group: "Content",
    label: "Watch & alert",
    icon: Eye,
    path: "/app/admin/watch-alert",
    subtitle:
      "Gold and Silver option ideas watched every minute: trend, news and events, an alert when the situation is right, and a paper position followed to its exit. No order is placed.",
    component: page(() => import("./WatchAlertPage"), "WatchAlertPage"),
    validateSearch: (s) => {
      const w = Number(s.watch);
      return Number.isInteger(w) && w > 0 ? { watch: w } : {};
    },
  },
  {
    // the AI model's internals (candidates, models, simulated record, raw paper
    // trades): admins only, never shown to users (owner, 2026-10-24)
    id: "ai-model",
    group: "Content",
    label: "AI model",
    icon: BrainCircuit,
    path: "/app/admin/ai-model",
    subtitle:
      "What the model ranks highly today, how well it has ranked out of sample, the simulated record, the raw paper trades and the language model that reads the news. Admins only.",
    component: page(() => import("../ai/AiPage"), "AiPage"),
    // tab, model, sim, leg (the old /app/ai links redirect here)
    validateSearch: parseAiSearch,
  },
  // Operations
  {
    id: "data-status",
    group: "Operations",
    label: "Data status",
    icon: DatabaseZap,
    path: "/app/admin/data-status",
    subtitle: "Is every dataset up to date? Fill the gaps after an outage.",
    component: page(() => import("./DataStatusPage"), "DataStatusPage"),
  },
  {
    id: "schedules",
    group: "Operations",
    label: "Schedules",
    icon: CalendarClock,
    path: "/app/admin/schedules",
    subtitle: "Every scheduled job: what it does, when it runs and how it went.",
    component: page(() => import("./SchedulesTab"), "SchedulesTab"),
  },
  {
    id: "data-refresh",
    group: "Operations",
    label: "Data refresh",
    icon: RefreshCw,
    path: "/app/admin/data-refresh",
    subtitle: "SEBI registers and mutual fund data: each run and what it changed.",
    component: page(() => import("./DataRefreshPage"), "DataRefreshPage"),
  },
  {
    // V103 (owner, 2 Nov 2026: no more silent failures)
    id: "delivery-health",
    group: "Operations",
    label: "Delivery health",
    icon: HeartPulse,
    path: "/app/admin/delivery-health",
    subtitle: "Is every live feature really delivering? Checked every 30 minutes in market hours.",
    component: page(() => import("./DeliveryHealthPage"), "DeliveryHealthPage"),
  },
  {
    id: "data-health",
    group: "Operations",
    label: "Data health",
    icon: Activity,
    path: "/app/admin/data-health",
    component: page(() => import("../data-health/DataHealthPage"), "DataHealthPage"),
  },
  {
    id: "backups",
    group: "Operations",
    label: "Backups",
    icon: Archive,
    path: "/app/admin/backups",
    subtitle: "The server's nightly database backups.",
    component: page(() => import("./BackupsPage"), "BackupsPage"),
  },
  // Logs (read-only audit trails; future ones such as job runs and sign-ins go here)
  {
    id: "activity-log",
    group: "Logs",
    label: "Activity log",
    icon: ScrollText,
    path: "/app/admin/activity-log",
    subtitle: "Every audited action. Pick anyone to see all they did: pages, sign-ins, payments and messages.",
    component: page(() => import("./ActivityLogPage"), "ActivityLogPage"),
    // person, view, show, action, from, to (IST days), page
    validateSearch: parseActivityLogSearch,
  },
  {
    id: "email-log",
    group: "Logs",
    label: "Email log",
    icon: MailSearch,
    path: "/app/admin/email-log",
    subtitle: "Every email the platform sent or tried to send, as it was sent. Codes and link tokens are never stored.",
    component: page(() => import("./EmailLogPage"), "EmailLogPage"),
    // q, kind, status, from, to (IST days), user_id, page
    validateSearch: parseEmailLogSearch,
  },
  // Settings
  {
    id: "settings-payments",
    group: "Settings",
    label: "Payment details",
    icon: Landmark,
    path: "/app/admin/settings/payments",
    subtitle: "Online payments (PayU), and the bank-transfer switch with the bank details.",
    component: page(() => import("./SettingsPages"), "PaymentSettingsPage"),
  },
  {
    id: "settings-notifications",
    group: "Settings",
    label: "Notifications",
    icon: Bell,
    path: "/app/admin/settings/notifications",
    subtitle: "Admin alerts, saving AI calls, and email and push delivery.",
    component: page(() => import("./SettingsPages"), "NotificationsSettingsPage"),
  },
  {
    id: "settings-strategy-lab",
    group: "Settings",
    label: "Options Strategy Lab",
    icon: Target,
    path: "/app/admin/settings/strategy-lab",
    subtitle: "The Trade Check's verdict wording and the thresholds behind each check.",
    component: page(() => import("./StrategyLabSettings"), "StrategyLabSettingsPage"),
  },
  {
    id: "settings-stock-lab",
    group: "Settings",
    label: "Stock Strategy Lab",
    icon: Lightbulb,
    path: "/app/admin/settings/stock-lab",
    subtitle: "The Idea Check's verdict wording and the thresholds behind each check.",
    component: page(() => import("./StockLabSettings"), "StockLabSettingsPage"),
  },
  {
    id: "settings-email",
    group: "Settings",
    label: "Email",
    icon: Mail,
    path: "/app/admin/settings/email",
    subtitle: "The email provider, recent sends and a test email.",
    component: page(() => import("./SettingsPages"), "EmailSettingsPage"),
  },
];

/** Admin pages merged into another: old path → new path (bookmarks keep working). */
export const MOVED_PATHS: Record<string, string> = {
  "/app/admin/settings/signup": "/app/admin/plans", // into Plans & trials, 29 Oct 2026
};

/** Old /app/admin?tab=<x> links → the page that replaced the tab. */
export const LEGACY_TABS: Record<string, string> = {
  overview: ADMIN_ROOT,
  verification: "/app/admin/verification",
  payments: "/app/admin/payments",
  users: "/app/admin/users",
  broadcast: "/app/admin/broadcast",
  plans: "/app/admin/plans",
  team: "/app/admin/team",
  schedules: "/app/admin/schedules",
  channels: "/app/admin/plans",
};

/** The section for a path (the overview for anything unknown). */
export function sectionFor(path: string): AdminSection {
  const clean = path.replace(/\/+$/, "") || ADMIN_ROOT;
  return ADMIN_SECTIONS.find((s) => s.path === clean) ?? (ADMIN_SECTIONS[0] as AdminSection);
}
